import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Check, Sparkles, Zap, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import Seo from '@/components/Seo';

const FREE_FEATURES = [
  'All 30+ trade calculators (bricks, concrete, BTU, voltage drop, paint, tiles, joists & more)',
  'Muck-away, overheads, VAT, day-rate & profit calculators',
  'Estimator with VAT & margin — view-only (no save)',
  'Mobile-first, works on-site',
  'Free forever — no card required',
];

const PRO_FEATURES = [
  'Unlimited saved quotes & projects',
  'Branded PDF quotes you can send straight to customers',
  'Customer details, status tracking & duplicate quotes',
  'Save your overheads profile to your account',
  'Quote/invoice numbers + dashboard sort & filter',
  'Business profile + logo on every invoice',
  'Manage subscription from your dashboard',
  'Every new calculator we add — included free',
  'Priority support',
  '14-day free trial · cancel anytime',
];

const PRO_PLUS_FEATURES = [
  'Everything in TradesMate Pro',
  'AI Estimator — describe any job in plain English, get a full breakdown',
  'Customer share links — send a quote with one click',
  'See exactly when your customer views the quote',
  'Customer accepts or declines online (with signature)',
  'Instant email when a customer views, accepts or declines',
  'Auto-update status as customers interact',
  '14-day free trial · cancel or downgrade anytime',
];

export default function PricingPage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [recovering, setRecovering] = useState(false);

  const start = async (tier) => {
    if (!user) { nav('/register?next=/pricing'); return; }
    setBusy(tier);
    try {
      const { data } = await api.post('/billing/checkout', { origin_url: window.location.origin, tier });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
    } finally { setBusy(null); }
  };

  const recoverSubscription = async () => {
    setRecovering(true);
    try {
      const { data } = await api.post('/billing/sync');
      if (data?.synced && data.is_pro) {
        if (refresh) await refresh();
        toast.success(`Subscription recovered — you're on ${data.tier === 'pro_plus' ? 'Pro+' : 'Pro'}!`);
      } else if (data?.reason === 'no_stripe_customer') {
        toast.error('We could not find a Stripe customer for your email. Are you logged in with the same email you paid with?');
      } else if (data?.reason === 'no_subscription') {
        toast.error('No active subscription found on Stripe for your account.');
      } else if (data?.reason === 'stripe_not_configured') {
        toast.error('Stripe is not configured on the server. Please contact support.');
      } else if (data?.reason === 'stripe_customer_lookup_failed' || data?.reason === 'stripe_lookup_failed') {
        toast.error(`Stripe lookup failed: ${data?.error || 'unknown error'}. Please contact support.`);
      } else if (data?.reason === 'db_update_failed') {
        toast.error(`Database update failed: ${data?.error || 'unknown error'}. Please contact support.`);
      } else {
        toast('Nothing to sync — your account is already up to date.');
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const msg = detail
        ? `Recovery failed (${status}): ${detail}`
        : status
          ? `Recovery failed — server returned ${status}. Please contact support.`
          : `Recovery failed — could not reach server. Check your connection.`;
      toast.error(msg);
      // also log for browser-console debugging
      console.error('billing/sync failed', { status, data: e.response?.data, err: e });
    } finally { setRecovering(false); }
  };

  const currentTier = user?.subscription_tier || 'free';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo
        title="Pricing — Free, Pro & Pro+ Plans"
        description="TradesMate UK pricing: use 40+ trade calculators free, or go Pro to save unlimited quotes, export branded PDF invoices and track job profit. Start a free trial."
        path="/pricing"
      />
      <Toaster richColors position="top-right" />
      <header className="text-center mb-12">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Pricing</p>
        <h1 className="font-display font-semibold text-4xl sm:text-6xl uppercase tracking-tight mt-2">Simple. <em className="italic font-medium text-brand">Honest</em>. Pro.</h1>
        <p className="text-slate-600 mt-4 max-w-xl mx-auto">14-day free trial on any paid plan. Cancel any time from your dashboard. No setup fees.</p>
      </header>

      <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {/* FREE */}
        <div className="tm-card p-8 flex flex-col">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Free</p>
          <h2 className="font-display font-semibold text-3xl uppercase tracking-tight mt-1">Tools</h2>
          <p className="font-mono font-semibold text-5xl mt-4">£0<span className="text-base font-normal text-slate-500">/forever</span></p>
          <ul className="mt-6 space-y-3 flex-1">
            {FREE_FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Check size={18} className="text-slate-900 mt-0.5 shrink-0"/> <span>{f}</span>
              </li>
            ))}
          </ul>
          <Link to="/trades" className="tm-btn tm-btn-ghost w-full mt-8">Use Free Tools</Link>
        </div>

        {/* PRO */}
        <div className="tm-card tm-card-dark p-8 border border-slate-200 shadow-md relative flex flex-col">
          <span className="absolute -top-3 -right-3 tm-chip-orange tm-chip">Most Popular</span>
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-brand"/>
            <p className="text-xs uppercase tracking-widest text-brand font-semibold">Pro</p>
          </div>
          <h2 className="font-display font-semibold text-3xl uppercase tracking-tight mt-1">TradesMate Pro</h2>
          <p className="font-mono font-semibold text-5xl mt-4 text-brand">£4.99<span className="text-base font-normal text-slate-300">/month</span></p>
          <p className="text-xs uppercase tracking-widest text-slate-400 mt-1 font-semibold">14-day free trial</p>
          <ul className="mt-6 space-y-3 flex-1">
            {PRO_FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Check size={18} className="text-brand mt-0.5 shrink-0"/>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {currentTier === 'pro' ? (
            <button disabled className="tm-btn tm-btn-dark w-full mt-8 opacity-70">You&apos;re on Pro ✓</button>
          ) : (
            <button data-testid="pricing-start-pro" onClick={() => start('pro')} disabled={busy} className="tm-btn w-full mt-8">
              {busy === 'pro' ? 'Loading…' : currentTier === 'pro_plus' ? 'Downgrade to Pro' : 'Start 14-Day Free Trial'}
            </button>
          )}
        </div>

        {/* PRO+ */}
        <div className="tm-card p-8 relative flex flex-col" style={{ borderColor: '#FF5A1F', borderWidth: 3 }}>
          <span className="absolute -top-3 -right-3 tm-chip-orange tm-chip">NEW</span>
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-brand"/>
            <p className="text-xs uppercase tracking-widest text-brand font-semibold">Pro+</p>
          </div>
          <h2 className="font-display font-semibold text-3xl uppercase tracking-tight mt-1">Pro<span className="text-brand">+</span></h2>
          <p className="font-mono font-semibold text-5xl mt-4 text-brand">£9.99<span className="text-base font-normal text-slate-500">/month</span></p>
          <p className="text-xs uppercase tracking-widest text-slate-500 mt-1 font-semibold">14-day free trial</p>
          <ul className="mt-6 space-y-3 flex-1">
            {PRO_PLUS_FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Check size={18} className="text-brand mt-0.5 shrink-0"/>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {currentTier === 'pro_plus' ? (
            <button disabled className="tm-btn w-full mt-8 opacity-70">You&apos;re on Pro+ ✓</button>
          ) : (
            <button data-testid="pricing-start-pro-plus" onClick={() => start('pro_plus')} disabled={busy} className="tm-btn w-full mt-8">
              {busy === 'pro_plus' ? 'Loading…' : currentTier === 'pro' ? 'Upgrade to Pro+' : 'Start 14-Day Free Trial'}
            </button>
          )}
        </div>
      </div>

      <p className="text-center text-xs text-slate-500 mt-10 uppercase tracking-wider font-semibold">
        Secured by Stripe · GBP billing · UK VAT-registered customers can deduct on expenses
      </p>

      {user && (
        <div className="max-w-2xl mx-auto mt-10 tm-card p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between border-slate-300">
          <div>
            <p className="font-display font-bold text-base uppercase tracking-tight">Already paid but no badge?</p>
            <p className="text-sm text-slate-600 mt-1">
              If Stripe took your payment but your account is still showing as Free, hit recover. We&apos;ll pull your subscription straight from Stripe and unlock your features.
            </p>
          </div>
          <button
            data-testid="pricing-recover-subscription"
            onClick={recoverSubscription}
            disabled={recovering}
            className="tm-btn shrink-0"
          >
            <RefreshCw size={16} className={recovering ? 'animate-spin' : ''}/>
            {recovering ? 'Checking Stripe…' : 'Recover my subscription'}
          </button>
        </div>
      )}

      <div className="text-center mt-8">
        <Link to="/feedback" className="text-xs uppercase tracking-widest text-slate-600 hover:text-brand underline font-semibold">
          Got a calculator idea? Tell us →
        </Link>
      </div>
    </div>
  );
}
